import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentApi } from '@formosa/shared/api/payment';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import {
  formatDepositTypeLabel,
  formatFundAmount,
  formatPaymentStatusLabel,
  formatSignedFundAmount,
  formatTransactionTypeLabel,
  getAmountTone,
  getPaymentStatusTone,
  tf,
} from '../../constants/fundConstants';
import { useTranslation } from '../../i18n/LocaleContext';

const TABS = [
  { key: 'all', labelKey: 'funds.tabAll' },
  { key: 'deposit', labelKey: 'funds.tabDeposit' },
  { key: 'withdraw', labelKey: 'funds.tabWithdraw' },
];

const PAGE_SIZE = 15;

export default function FundDetailsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('all');
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const loadRecords = useCallback(
    async (tab, pageNo, append = false) => {
      if (pageNo === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError('');
      try {
        let data;
        if (tab === 'deposit') {
          data = await paymentApi.depositPage({ pageNo, pageSize: PAGE_SIZE });
        } else if (tab === 'withdraw') {
          data = await paymentApi.withdrawPage({ pageNo, pageSize: PAGE_SIZE });
        } else {
          data = await paymentApi.transactionPage({ pageNo, pageSize: PAGE_SIZE });
        }
        const rows = data?.records || [];
        setRecords((prev) => (append ? [...prev, ...rows] : rows));
        setPage(data?.current || pageNo);
        setTotalPages(data?.pages || 1);
      } catch (err) {
        if (!append) {
          setRecords([]);
        }
        setError(err.message || t('funds.loadFailed'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadRecords(activeTab, 1, false);
  }, [activeTab, loadRecords]);

  function handleTabChange(tab) {
    if (tab === activeTab) {
      return;
    }
    setActiveTab(tab);
    setRecords([]);
    setPage(1);
    setTotalPages(1);
  }

  function handleLoadMore() {
    if (loadingMore || page >= totalPages) {
      return;
    }
    loadRecords(activeTab, page + 1, true);
  }

  return (
    <div className="funds-page funds-page--details">
      <header className="funds-page__header">
        <button
          type="button"
          className="funds-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('funds.detailsTitle')}</h1>
        <span className="funds-page__header-spacer" aria-hidden />
      </header>

      <div className="funds-page__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`funds-page__tab ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {loading && <p className="funds-page__status">{t('common.loading')}</p>}
      {error && <p className="funds-page__error">{error}</p>}

      {!loading && records.length === 0 && !error && (
        <div className="funds-page__empty">
          <div className="funds-page__empty-icon" aria-hidden>
            📄
          </div>
          <p>{t('common.noData')}</p>
        </div>
      )}

      <ul className="funds-record-list">
        {records.map((row) => (
          <li key={`${activeTab}-${row.id}`}>
            {activeTab === 'all' && <AllRecordItem row={row} t={t} />}
            {activeTab === 'deposit' && <DepositRecordItem row={row} t={t} />}
            {activeTab === 'withdraw' && <WithdrawRecordItem row={row} t={t} />}
          </li>
        ))}
      </ul>

      {!loading && records.length > 0 && (
        <div className="funds-page__footer">
          {page < totalPages ? (
            <button
              type="button"
              className="funds-page__load-more"
              disabled={loadingMore}
              onClick={handleLoadMore}
            >
              {loadingMore ? t('common.loading') : t('funds.loadMore')}
            </button>
          ) : (
            <p>{t('funds.noMore')}</p>
          )}
        </div>
      )}
    </div>
  );
}

function AllRecordItem({ row, t }) {
  const amount = row.amount ?? row.coinPaid;
  const tone = getAmountTone(amount);
  const fee = row.fee;

  return (
    <article className="funds-record-item">
      <div className="funds-record-item__main">
        <div className="funds-record-item__left">
          <div className="funds-record-item__title">{formatTransactionTypeLabel(t, row.type)}</div>
          <div className="funds-record-item__time">{formatDateTime(row.createTime)}</div>
        </div>
        <div className="funds-record-item__right">
          <div className={`funds-record-item__amount is-${tone}`}>
            {formatSignedFundAmount(amount)}
          </div>
          <div className="funds-record-item__meta">
            {tf(t, 'funds.feeLabel', { amount: formatFundAmount(fee) })}
          </div>
          <div className="funds-record-item__meta">
            {tf(t, 'funds.balanceLabel', { amount: formatFundAmount(row.balance) })}
          </div>
        </div>
      </div>
    </article>
  );
}

function DepositRecordItem({ row, t }) {
  const currency = row.currencyName || 'USDT';
  const amount = row.realAmount ?? row.amount;
  const statusTone = getPaymentStatusTone(row.status);

  return (
    <article className="funds-record-item">
      <div className="funds-record-item__main">
        <div className="funds-record-item__left">
          <div className="funds-record-item__title">{formatDepositTypeLabel(t, row.type)}</div>
          <div className="funds-record-item__time">{formatDateTime(row.createTime)}</div>
        </div>
        <div className="funds-record-item__right">
          <div className={`funds-record-item__status is-${statusTone}`}>
            {formatPaymentStatusLabel(t, row.status)}
          </div>
          <div className="funds-record-item__meta funds-record-item__meta--highlight">
            {tf(t, 'funds.depositAmountLabel', {
              currency,
              amount: formatFundAmount(amount),
            })}
          </div>
        </div>
      </div>
    </article>
  );
}

function WithdrawRecordItem({ row, t }) {
  const currency = row.currencyName || 'USDT';
  const received = row.receivedAmount ?? row.withdrawalAmount ?? row.amount;
  const statusTone = getPaymentStatusTone(row.status);

  return (
    <article className="funds-record-item">
      <div className="funds-record-item__main">
        <div className="funds-record-item__left">
          <div className="funds-record-item__title">{currency}</div>
          <div className="funds-record-item__time">{formatDateTime(row.createTime)}</div>
        </div>
        <div className="funds-record-item__right">
          <div className={`funds-record-item__status is-${statusTone}`}>
            {formatPaymentStatusLabel(t, row.status)}
          </div>
          <div className="funds-record-item__meta">
            {tf(t, 'funds.withdrawAmountLabel', {
              currency,
              amount: formatFundAmount(row.amount),
            })}
          </div>
          <div className="funds-record-item__meta">
            {tf(t, 'funds.feeLabel', { amount: formatFundAmount(row.fee) })}
          </div>
          <div className="funds-record-item__meta">
            {tf(t, 'funds.receivedAmountLabel', {
              amount: formatFundAmount(received),
            })}
          </div>
        </div>
      </div>
    </article>
  );
}
